export const DEFAULT_NUTRIENT_FORMULAS = [
  { id: '1', parameter: 'NO₃', formula: '((x + 0.2591) / 0.605) * 10 * 2.24', unit: 'kg/ha', displayOrder: 1 },
  { id: '2', parameter: 'NH₄', formula: '((x + 0.0165) / 0.45) * 50 * 2.47', unit: 'kg/ha', displayOrder: 2 },
  { id: '3', parameter: 'P', formula: '((x - 0.0154) / 0.474) * 11.2', unit: 'kg/ha', displayOrder: 3 },
  { id: '4', parameter: 'K', formula: '((x + 0.0414) / 0.0146) * 10 * 2.24 * 1.2', unit: 'kg/ha', displayOrder: 4 },
  { id: '5', parameter: 'OC', formula: '(x + 0.076) / 0.924', unit: '%', displayOrder: 5 },
  { id: '6', parameter: 'EC', formula: 'x / 1000', unit: 'dS/m', displayOrder: 6 },
  { id: '7', parameter: 'Sulphur', formula: '(Math.abs(x) + 0.0285) / 0.01', unit: 'ppm', displayOrder: 7 },
  { id: '8', parameter: 'Calcium', formula: '200.4 * x', unit: 'ppm', displayOrder: 8 },
  { id: '9', parameter: 'Magnesium', formula: '121.5 * x', unit: 'ppm', displayOrder: 9 },
  { id: '10', parameter: 'Boron', formula: '((x - 0.00296) / 0.025) / 6.5', unit: 'ppm', displayOrder: 10 },
  { id: '11', parameter: 'Iron', formula: '((x - 0.00271) / 0.0275) * 2', unit: 'ppm', displayOrder: 11 },
  { id: '12', parameter: 'Zinc', formula: '(x - 0.118) / 0.53', unit: 'ppm', displayOrder: 12 },
  { id: '13', parameter: 'Copper', formula: 'Math.abs((x - 0.0248) / 0.0337)', unit: 'ppm', displayOrder: 13 },
  { id: '14', parameter: 'Zinc (new)', formula: '((x + 0.575) / 0.101) / 14', unit: 'ppm', displayOrder: 14 },
  { id: '15', parameter: 'OC (high range)', formula: '(0.37 * x) + 0.55', unit: '%', displayOrder: 15 },
  { id: '16', parameter: 'K (new)', formula: '((x + 0.0626) / 0.0124) * 10 * 2.24 * 1.2', unit: 'kg/ha', displayOrder: 16 },
];

function getContextFromRows(allRows) {
  let calcium = 0;
  let magnesium = 0;
  if (allRows?.length) {
    const calciumRow = allRows.find((r) => r.parameter === 'Calcium');
    const magnesiumRow = allRows.find((r) => r.parameter === 'Magnesium');
    const calciumVal = calciumRow?.rawInput ?? calciumRow?.computedRaw;
    const magnesiumVal = magnesiumRow?.rawInput ?? magnesiumRow?.computedRaw;
    if (calciumVal) calcium = parseFloat(calciumVal) || 0;
    if (magnesiumVal) magnesium = parseFloat(magnesiumVal) || 0;
  }
  return { calcium, magnesium };
}

function evalFormula(formula, x, allRows) {
  const { calcium, magnesium } = getContextFromRows(allRows);
  // eslint-disable-next-line no-eval
  return eval(formula);
}

function formatNumericOutput(value) {
  if (Number.isNaN(value) || !Number.isFinite(value)) {
    return 'Error';
  }
  if (Math.abs(value) < 0.0001 && value !== 0) {
    return value.toExponential(4);
  }
  if (Math.abs(value) > 999999) {
    return value.toExponential(4);
  }
  return value.toFixed(4);
}

export function calculateResult(formula, rawValue, allRows) {
  if (!rawValue || String(rawValue).trim() === '') {
    return '';
  }

  const x = parseFloat(rawValue);
  if (Number.isNaN(x)) {
    return 'Invalid input';
  }

  try {
    const result = evalFormula(formula, x, allRows);
    return formatNumericOutput(result);
  } catch {
    return 'Formula error';
  }
}

/** Solve f(x) = target for x using bisection (sensor raw is typically non-negative). */
export function calculateRawFromResult(formula, targetValue, allRows) {
  if (!targetValue || String(targetValue).trim() === '') {
    return '';
  }

  const target = parseFloat(targetValue);
  if (Number.isNaN(target)) {
    return 'Invalid input';
  }

  const fn = (x) => {
    try {
      return evalFormula(formula, x, allRows) - target;
    } catch {
      return Number.NaN;
    }
  };

  const ranges = [
    [0, 50],
    [-50, 50],
    [-500, 500],
    [-5000, 5000],
  ];

  for (const [loStart, hiStart] of ranges) {
    let lo = loStart;
    let hi = hiStart;
    let fLo = fn(lo);
    let fHi = fn(hi);

    if (Number.isNaN(fLo) || Number.isNaN(fHi)) {
      continue;
    }

    if (fLo === 0) return formatNumericOutput(lo);
    if (fHi === 0) return formatNumericOutput(hi);

    if (fLo * fHi > 0) {
      const steps = 40;
      const step = (hi - lo) / steps;
      let found = false;
      for (let i = 0; i < steps; i += 1) {
        const a = lo + i * step;
        const b = a + step;
        const fa = fn(a);
        const fb = fn(b);
        if (Number.isNaN(fa) || Number.isNaN(fb)) continue;
        if (fa === 0) return formatNumericOutput(a);
        if (fb === 0) return formatNumericOutput(b);
        if (fa * fb <= 0) {
          lo = a;
          hi = b;
          fLo = fa;
          fHi = fb;
          found = true;
          break;
        }
      }
      if (!found) continue;
    }

    for (let i = 0; i < 80; i += 1) {
      const mid = (lo + hi) / 2;
      const fMid = fn(mid);
      if (Number.isNaN(fMid)) break;
      if (Math.abs(fMid) < 1e-8 || hi - lo < 1e-8) {
        return formatNumericOutput(mid);
      }
      if (fLo * fMid <= 0) {
        hi = mid;
        fHi = fMid;
      } else {
        lo = mid;
        fLo = fMid;
      }
    }

    return formatNumericOutput((lo + hi) / 2);
  }

  return 'No solution';
}

export function buildCalculationRows(formulas, inputsById) {
  const rows = formulas.map((f) => ({
    ...f,
    rawInput: inputsById[f.id] != null ? String(inputsById[f.id]) : '',
  }));

  return rows.map((row) => ({
    ...row,
    result: calculateResult(row.formula, row.rawInput, rows),
  }));
}

export function buildReverseRows(formulas, targetsById) {
  const rows = formulas.map((f) => ({
    ...f,
    targetValue: targetsById[f.id] != null ? String(targetsById[f.id]) : '',
    rawInput: '',
    computedRaw: '',
  }));

  return rows.map((row) => {
    const computedRaw = calculateRawFromResult(row.formula, row.targetValue, rows);
    return {
      ...row,
      computedRaw,
      rawInput: computedRaw,
    };
  });
}
